import React, { useEffect, useState } from 'react';
import ContactService from '../../Services/ContactService';
import { getEventsScreenData } from '../../Public/events-screen-data';
import { formatDateRange } from '../Events/event-date-utils';

/**
 * Quick "join event" lead form, name / email / phone / guests → ContactService.joinEvent.
 * On success it transfers the user to the event's payment page (registration.paymentUrl).
 * Dark-themed to sit inside the event modals.
 */
export default function EventJoinForm({ event, lang = 'he', dir = 'rtl' }) {
    const { join: j } = getEventsScreenData(lang);
    const paymentUrl = event?.registration?.paymentUrl || null;

    const [form, setForm] = useState({ fullName: '', email: '', phone: '' });
    const [guests, setGuests] = useState(1);
    const [status, setStatus] = useState('idle'); // idle | loading | success
    const [error, setError] = useState('');

    const emailOk = /\S+@\S+\.\S+/.test(form.email);
    const valid = form.fullName.trim().length > 1 && emailOk;

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    // On success, hand the user off to the event's payment page.
    useEffect(() => {
        if (status !== 'success' || !paymentUrl) return;
        const id = setTimeout(() => { window.location.assign(paymentUrl); }, 1200);
        return () => clearTimeout(id);
    }, [status, paymentUrl]);

    const submit = async (e) => {
        e.preventDefault();
        if (!valid || status === 'loading') return;
        setStatus('loading');
        setError('');
        try {
            await ContactService.joinEvent({
                fullName: form.fullName.trim(),
                email: form.email.trim(),
                phone: form.phone.trim(),
                numberOfGuests: guests,
                eventTitle: event?.title,
                eventSlug: event?.slug,
                eventDate: event?.startDate,
                leadSource: 'Event Registration',
                page_url: typeof window !== 'undefined' ? window.location.href : '',
            });
            setStatus('success');
        } catch (_) {
            setStatus('idle');
            setError(j.error);
        }
    };

    if (status === 'success') {
        return (
            <div dir={dir} className="text-center py-6">
                <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-secondary-500/20 text-secondary-300 flex items-center justify-center">
                    <svg viewBox="0 0 24 24" fill="none" className="w-7 h-7"><path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </div>
                <h4 className="font-secondary text-xl font-extralight text-white">{j.successTitle}</h4>
                <p className="mt-1.5 text-white/60 font-primary">{paymentUrl ? j.redirecting : j.successBody}</p>
                {event && (
                    <p className="mt-3 text-sm font-bold text-secondary-300 font-primary">
                        {event.title} · {formatDateRange(event.startDate, event.endDate, lang)}
                    </p>
                )}
                {paymentUrl && (
                    <a
                        href={paymentUrl}
                        className="mt-5 inline-block rounded-full bg-secondary-500 text-white font-bold px-7 py-3 shadow-lg shadow-secondary-500/25 hover:bg-secondary-600 transition-colors font-primary"
                    >
                        {j.pay} →
                    </a>
                )}
            </div>
        );
    }

    const inputCls = 'w-full rounded-xl bg-white/5 border border-white/15 focus:border-secondary-500 text-white placeholder-white/40 px-4 py-3 outline-none transition-colors font-primary';

    return (
        <form onSubmit={submit} dir={dir} className="flex flex-col gap-3">
            {j.intro && <p className="text-white/55 text-sm font-primary -mt-1 mb-1">{j.intro}</p>}

            <input className={inputCls} value={form.fullName} onChange={set('fullName')} placeholder={j.name} autoComplete="name" required />
            <input className={inputCls} type="email" value={form.email} onChange={set('email')} placeholder={j.email} autoComplete="email" required />
            <input className={inputCls} type="tel" value={form.phone} onChange={set('phone')} placeholder={j.phone} autoComplete="tel" />

            {/* Guests */}
            <div className="flex items-center justify-between rounded-xl bg-white/5 border border-white/15 px-4 py-2.5">
                <span className="text-white/80 font-bold font-primary text-sm">{j.guests}</span>
                <div className="flex items-center gap-3">
                    <button type="button" onClick={() => setGuests((g) => Math.max(1, g - 1))}
                        aria-label="decrease guests"
                        className="w-8 h-8 rounded-full border border-white/20 text-white flex items-center justify-center hover:bg-white/10 transition-colors disabled:opacity-40"
                        disabled={guests <= 1}>−</button>
                    <span className="w-6 text-center text-white font-bold font-primary">{guests}</span>
                    <button type="button" onClick={() => setGuests((g) => Math.min(20, g + 1))}
                        aria-label="increase guests"
                        className="w-8 h-8 rounded-full border border-white/20 text-white flex items-center justify-center hover:bg-white/10 transition-colors disabled:opacity-40"
                        disabled={guests >= 20}>+</button>
                </div>
            </div>

            {error && <p className="text-sm font-bold text-red-400 font-primary">{error}</p>}

            <button
                type="submit"
                disabled={!valid || status === 'loading'}
                className="mt-1 w-full rounded-full bg-secondary-500 text-white font-bold py-3.5 shadow-lg shadow-secondary-500/25 hover:bg-secondary-600 transition-colors font-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {status === 'loading' ? j.sending : (paymentUrl ? j.submit : j.submitFree)}
            </button>

            <p className="text-[11px] text-white/40 text-center font-primary leading-relaxed">{j.disclaimer}</p>
        </form>
    );
}
